import React, { useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Modal } from 'antd';

import SignUpComponent from './signUp/signup';
import SignInComponent from './signIn/signin';
import ForgotPasswordComponent from './forgetPassword';
import CustomTab from '../tabs';

import {
  setTemporaryPassword,
  setIsForgotPassword,
  setRegisteredEmail,
  setIsFromRegister,
  setIsInitialFlow,
  setActiveTabKey,
  setIsFromLogin,
  setModalOpen,
  setStep
} from '../../redux/slices/modalSlice';

import { BRImages } from '../../assets/imgs';
import '../../assets/scss/index.scss';

const AuthModal = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerHeight < 500) { 
        document.body.style.height = `${window.innerHeight}px`;
      } else {
        document.body.style.height = '100vh';
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const { isModalOpen, activeTabKey, isForgotPassword, isFromRegister, isFromLogin } = useSelector(
    (state) => state.modal
  );

  // Memoized tab options
  const tabOptions = useMemo(
    () => [
      { key: '1', label: 'Sign In' },
      { key: '2', label: 'Sign Up' }
    ],
    []
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const username = searchParams.get('un');
    const emailParam = username?.replace(/\s/g, '+');
    const tempPassword = searchParams.get('pw');
    const initialFlow = searchParams.get('initialFlow');

    if (location.pathname === '/set-new-password' && username && tempPassword) {
      dispatch(setModalOpen(true));
      dispatch(setIsForgotPassword(true));
      dispatch(setStep('setNewPassword'));
      dispatch(setRegisteredEmail(emailParam));
      dispatch(setTemporaryPassword(tempPassword));
      dispatch(setIsInitialFlow(initialFlow));
    }
  }, [location, dispatch]);

  const closeModal = useCallback(() => dispatch(setModalOpen(false)), [dispatch]);

  const handleForgotPasswordBack = useCallback(() => {
    dispatch(setIsForgotPassword(false));
    dispatch(setIsFromRegister(false));
  }, [dispatch]);

  const handleRegisterSuccess = useCallback(
    (email) => {
      dispatch(setStep('checkEmail'));
      dispatch(setIsFromLogin(false));
      dispatch(setIsFromRegister(true));
      dispatch(setIsForgotPassword(true));
      dispatch(setRegisteredEmail(email));
    },
    [dispatch]
  );

  const renderTabContent = useMemo(() => {
    if (isForgotPassword) {
      return (
        <ForgotPasswordComponent
          onBack={handleForgotPasswordBack}
          isFromRegister={isFromRegister}
          isFromLogin={isFromLogin}
        />
      );
    }

    const components = {
      1: <SignInComponent onForgotPasswordClick={() => dispatch(setIsForgotPassword(true))} />,
      2: <SignUpComponent onRegisterSuccess={handleRegisterSuccess} />
    };

    return components[activeTabKey] || null;
  }, [
    activeTabKey,
    isForgotPassword,
    isFromRegister,
    isFromLogin,
    handleForgotPasswordBack,
    handleRegisterSuccess,
    dispatch
  ]);

  const modalClassName = `bookroad-authmodal-container ${
    isForgotPassword ? 'forgot-password-modal' : ''
  }`;
  const modalWidth = isForgotPassword ? 450 : 520;

  return (
    <Modal
      open={isModalOpen}
      onCancel={closeModal}
      footer={null}
      maskClosable={false}
      closeIcon={<img src={BRImages.CommonImages.CloseGrayIcon} alt='close-icon' />}
      destroyOnClose
      className={modalClassName}
      width={modalWidth}
      getContainer={() => document.body} 
    >
      <div
        className={`bookroad-signup-tab-container ${
          isForgotPassword ? 'radius-16' : 'radius-5 mb-16'
        }`}
      >
        {!isForgotPassword && (
          <CustomTab
            tabs={tabOptions}
            activeTab={activeTabKey}
            onTabChange={(key) => dispatch(setActiveTabKey(key))}
            className='bookroad-signup-tab-container'
          />
        )}
        {renderTabContent}
      </div>
    </Modal>
  );
};

export default AuthModal;
